import { ArrowLeft, Ear, Heart, MessageCircle, Brain, Sparkles, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';

export default function DeepListeningGuide() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-teal-700 hover:text-teal-800 hover:bg-teal-100"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Week
        </Button>

        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-teal-100 rounded-full">
              <Ear className="w-8 h-8 text-teal-600" />
            </div>
            <div>
              <h1 className="text-3xl font-serif font-bold text-gray-900">Deep Listening Practice</h1>
              <p className="text-teal-600">Week 10, Day 5 - Social Connections</p>
            </div>
          </div>

          <div className="prose prose-teal max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Most of us listen to respond, not to understand. Today, practice deep listening - focusing 
              entirely on the other person without planning what you'll say next.
            </p>

            <div className="bg-teal-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-teal-800 mb-4 flex items-center gap-2">
                <Heart className="w-5 h-5" />
                Why Deep Listening Matters
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Makes others feel truly heard and valued</li>
                <li>• Builds trust and deepens connections</li>
                <li>• Reduces misunderstandings</li>
                <li>• Often reveals what people really need</li>
                <li>• Transforms the quality of your relationships</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">The Deep Listening Process</h2>
            
            <div className="space-y-4 mb-8">
              <div className="bg-white border-2 border-teal-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                  <h3 className="font-semibold text-gray-900">Clear Your Mind</h3>
                </div>
                <p className="text-gray-600 ml-11">Before the conversation, take a breath and set an intention to be fully present. Release any agenda.</p>
              </div>
              
              <div className="bg-white border-2 border-teal-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
                  <h3 className="font-semibold text-gray-900">Focus Completely</h3>
                </div>
                <p className="text-gray-600 ml-11">Give your full attention. Make eye contact. Notice their body language, tone, and emotions.</p>
              </div>
              
              <div className="bg-white border-2 border-teal-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
                  <h3 className="font-semibold text-gray-900">Don't Plan Your Response</h3>
                </div>
                <p className="text-gray-600 ml-11">This is the hardest part. When your mind starts formulating a reply, gently bring attention back to them.</p>
              </div>
              
              <div className="bg-white border-2 border-teal-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">4</div>
                  <h3 className="font-semibold text-gray-900">Ask Follow-Up Questions</h3>
                </div>
                <p className="text-gray-600 ml-11">Show curiosity. "Tell me more about that." "How did that make you feel?"</p>
              </div>
              
              <div className="bg-white border-2 border-teal-200 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center font-bold">5</div>
                  <h3 className="font-semibold text-gray-900">Reflect Back</h3>
                </div>
                <p className="text-gray-600 ml-11">"It sounds like you're feeling..." "What I'm hearing is..." This confirms understanding.</p>
              </div>
            </div>

            <div className="bg-red-50 rounded-xl p-6 mb-8">
              <h2 className="text-xl font-semibold text-red-800 mb-4 flex items-center gap-2">
                <Brain className="w-5 h-5" />
                Common Listening Blocks to Avoid
              </h2>
              <ul className="space-y-2 text-gray-700">
                <li>• Planning your response while they're talking</li>
                <li>• Jumping in with your own similar story</li>
                <li>• Offering solutions before they've finished</li>
                <li>• Changing the subject</li>
                <li>• Checking your phone</li>
                <li>• Interrupting to clarify small details</li>
                <li>• Finishing their sentences</li>
              </ul>
            </div>

            <h2 className="text-2xl font-serif font-bold text-gray-900 mb-4">Helpful Phrases</h2>
            
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">To Encourage Sharing</h3>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• "Tell me more..."</li>
                  <li>• "What was that like for you?"</li>
                  <li>• "How did you feel about that?"</li>
                  <li>• "And then what happened?"</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-gray-900 mb-2">To Show Understanding</h3>
                <ul className="space-y-1 text-gray-600 text-sm">
                  <li>• "It sounds like..."</li>
                  <li>• "I can imagine that was..."</li>
                  <li>• "That makes sense because..."</li>
                  <li>• "No wonder you felt..."</li>
                </ul>
              </div>
            </div>

            <div className="bg-gradient-to-r from-teal-100 to-cyan-100 rounded-xl p-6">
              <h2 className="text-xl font-semibold text-teal-800 mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5" />
                Today's Practice
              </h2>
              <p className="text-gray-700 mb-4">
                In your next conversation, try to listen for at least 2-3 minutes without interrupting or 
                planning your response. Notice how different it feels - both for you and for them.
              </p>
              <p className="text-teal-700 font-medium">
                After the conversation, reflect: What did you notice? What was challenging? 
                What did you learn about the other person that you might have missed otherwise?
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
